import cronstrue from "cronstrue";
import * as icons from "lucide-react";
import { Circle, X } from "lucide-react";

// Shared vocabulary for every Flows page: the icon lookup, the status chips and
// the date/number formatting. Kept in one file so the canvas, the palette, the
// template gallery and the run inspector cannot drift apart on what "failed"
// looks like or which colour a category is.

type IconComponent = typeof Circle;

const ICONS = icons as unknown as Record<string, IconComponent | undefined>;

/**
 * A node definition carries its icon as a lucide name. Resolving it at render
 * time rather than importing a fixed list means a node added on the server
 * appears in the palette with its icon and no frontend change.
 */
export const NodeIcon = ({
	name,
	className,
}: {
	name: string;
	className?: string;
}) => {
	const Component = ICONS[name];
	const Resolved =
		typeof Component === "function" || typeof Component === "object"
			? (Component as IconComponent)
			: Circle;
	return <Resolved className={className} />;
};

/** Category accents arrive as bare Tailwind colour names; these are the classes. */
const ACCENTS: Record<
	string,
	{ border: string; bg: string; text: string; dot: string }
> = {
	slate: {
		border: "border-slate-300",
		bg: "bg-slate-50",
		text: "text-slate-700",
		dot: "bg-slate-400",
	},
	sky: {
		border: "border-sky-300",
		bg: "bg-sky-50",
		text: "text-sky-700",
		dot: "bg-sky-500",
	},
	amber: {
		border: "border-amber-300",
		bg: "bg-amber-50",
		text: "text-amber-700",
		dot: "bg-amber-500",
	},
	violet: {
		border: "border-violet-300",
		bg: "bg-violet-50",
		text: "text-violet-700",
		dot: "bg-violet-500",
	},
	emerald: {
		border: "border-emerald-300",
		bg: "bg-emerald-50",
		text: "text-emerald-700",
		dot: "bg-emerald-500",
	},
	pink: {
		border: "border-pink-300",
		bg: "bg-pink-50",
		text: "text-pink-700",
		dot: "bg-pink-500",
	},
	teal: {
		border: "border-teal-300",
		bg: "bg-teal-50",
		text: "text-teal-700",
		dot: "bg-teal-500",
	},
	orange: {
		border: "border-orange-300",
		bg: "bg-orange-50",
		text: "text-orange-700",
		dot: "bg-orange-500",
	},
};

export const accentOf = (accent: string | undefined) =>
	ACCENTS[accent ?? "slate"] ?? ACCENTS.slate;

const FLOW_STATUS: Record<string, string> = {
	draft: "bg-slate-100 text-slate-600",
	published: "bg-emerald-50 text-emerald-700",
	paused: "bg-amber-50 text-amber-700",
	archived: "bg-slate-100 text-slate-400",
};

const RUN_STATUS: Record<string, string> = {
	queued: "bg-slate-100 text-slate-600",
	running: "bg-sky-50 text-sky-700",
	succeeded: "bg-emerald-50 text-emerald-700",
	failed: "bg-rose-50 text-rose-600",
	cancelled: "bg-slate-100 text-slate-500",
	skipped: "bg-slate-100 text-slate-500",
	waiting_approval: "bg-violet-50 text-violet-700",
};

const RUN_LABELS: Record<string, string> = {
	waiting_approval: "waiting",
};

export const StatusChip = ({
	status,
	kind = "flow",
	title,
}: {
	status: string | null | undefined;
	kind?: "flow" | "run";
	title?: string;
}) => {
	if (!status) return <span className="text-slate-400">—</span>;
	const styles = kind === "run" ? RUN_STATUS : FLOW_STATUS;
	return (
		<span
			title={title}
			className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
				styles[status] ?? "bg-slate-100 text-slate-600"
			}`}
		>
			{kind === "run" ? (RUN_LABELS[status] ?? status) : status}
		</span>
	);
};

export const Panel = ({
	title,
	hint,
	action,
	children,
}: {
	title?: string;
	hint?: string;
	action?: React.ReactNode;
	children: React.ReactNode;
}) => (
	<div className="card bg-white p-5">
		{(title || action) && (
			<div className="mb-4 flex items-baseline justify-between gap-3">
				<div className="flex items-baseline gap-3">
					{title && (
						<h3 className="text-sm font-semibold text-slate-700">{title}</h3>
					)}
					{hint && <span className="text-[11px] text-slate-400">{hint}</span>}
				</div>
				{action}
			</div>
		)}
		{children}
	</div>
);

export const fmtDateTime = (value: string | null | undefined) =>
	value ? new Date(value).toLocaleString("da-DK") : "—";

export const fmtDate = (value: string | null | undefined) =>
	value ? new Date(value).toLocaleDateString("da-DK") : "—";

export const fmtNumber = (n: number | null | undefined) =>
	n === null || n === undefined ? "—" : n.toLocaleString("da-DK");

export const kr = (n: number | null | undefined) =>
	n === null || n === undefined
		? "—"
		: `${n.toLocaleString("da-DK", { maximumFractionDigits: 0 })} kr`;

const UNITS: Array<[number, Intl.RelativeTimeFormatUnit]> = [
	[60_000, "second"],
	[3_600_000, "minute"],
	[86_400_000, "hour"],
	[604_800_000, "day"],
	[2_629_800_000, "week"],
	[31_557_600_000, "month"],
];

const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "3 hours ago" / "in 2 days" — the list is read for recency, not timestamps. */
export const fmtRelative = (value: string | null | undefined): string => {
	if (!value) return "never";
	const delta = new Date(value).getTime() - Date.now();
	if (Number.isNaN(delta)) return "—";
	const abs = Math.abs(delta);
	if (abs < 45_000) return "just now";
	for (const [limit, unit] of UNITS) {
		if (abs < limit) {
			const divisor =
				unit === "second"
					? 1000
					: unit === "minute"
						? 60_000
						: unit === "hour"
							? 3_600_000
							: unit === "day"
								? 86_400_000
								: 604_800_000;
			return relative.format(Math.round(delta / divisor), unit);
		}
	}
	return relative.format(Math.round(delta / 31_557_600_000), "year");
};

/**
 * Cron in English. An expression the server would reject still has to render
 * something, because the field is read while it is being typed.
 */
export const describeCron = (expression: string): string => {
	try {
		return cronstrue.toString(expression, { verbose: false });
	} catch {
		return "not a schedule this can read";
	}
};

export const Spinner = () => (
	<div className="flex items-center justify-center py-24">
		<div className="h-8 w-8 animate-spin rounded-full border-3 border-primary/20 border-t-primary" />
	</div>
);

export const ErrorBanner = ({ error }: { error: string | null }) =>
	error ? (
		<div className="rounded-xl border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
			{error}
		</div>
	) : null;

export const Empty = ({ children }: { children: React.ReactNode }) => (
	<div className="rounded-md border border-dashed border-hairline bg-canvas px-5 py-10 text-center text-[13px] text-muted">
		{children}
	</div>
);

export const btn = {
	primary:
		"inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-2 text-[13px] font-semibold text-black transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50",
	ghost:
		"inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-2 text-[13px] font-medium text-nav transition-colors hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-50",
	danger:
		"inline-flex items-center gap-1.5 rounded-md border border-danger/30 bg-white px-3 py-2 text-[13px] font-medium text-danger transition-colors hover:bg-danger/5 disabled:cursor-not-allowed disabled:opacity-50",
};

export const input =
	"w-full rounded-md border border-[#ced4da] px-3 py-2 text-[13px] text-ink outline-none focus:border-primary focus:ring-1 focus:ring-primary/30";

export const ConfirmDialog = ({
	title,
	body,
	confirmLabel,
	danger,
	busy,
	onCancel,
	onConfirm,
}: {
	title: string;
	body: React.ReactNode;
	confirmLabel: string;
	danger?: boolean;
	busy?: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}) => (
	<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
		<div className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-5 shadow-xl">
			<div className="flex items-start justify-between">
				<h3 className="text-[15px] font-semibold text-slate-800">{title}</h3>
				<button
					type="button"
					onClick={onCancel}
					className="rounded p-1 text-slate-400 hover:bg-slate-100"
					aria-label="Close"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
			<div className="space-y-2 text-[13px] leading-relaxed text-slate-600">
				{body}
			</div>
			<div className="flex justify-end gap-2">
				<button type="button" onClick={onCancel} className={btn.ghost}>
					Cancel
				</button>
				<button
					type="button"
					disabled={busy}
					onClick={onConfirm}
					className={danger ? btn.danger : btn.primary}
				>
					{confirmLabel}
				</button>
			</div>
		</div>
	</div>
);
